import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import config from '../../config';
import { getAdminUploadHeaders } from '../../utils/apiAuth';
import CurrentImageRender from './CurrentImageRender';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const MagazineCoverUploader = ({ 
  label, 
  suggestedFilename, 
  onUploadSuccess, 
  currentUrl 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [s3Url, setS3Url] = useState('');
  const [cdnUrl, setCdnUrl] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [invalidationStatus, setInvalidationStatus] = useState('');
  const [invalidationId, setInvalidationId] = useState('');
  const [cdnEnabled, setCdnEnabled] = useState(false);
  const [refreshKey, setRefreshKey] = useState(Date.now());

  const apiBaseUrl = config.getApiUrl();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate filename matches the suggested filename exactly
      if (file.name !== suggestedFilename) {
        setError(`Please rename your file to exactly: ${suggestedFilename}. Current filename: ${file.name}`);
        setSelectedFile(null);
        // Clear the file input
        event.target.value = '';
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setError('');
      setSuccess('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadStatus('Uploading to S3...');
    setInvalidationStatus('');
    setInvalidationId('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('filename', suggestedFilename);
      formData.append('folder', 'magazines');

      const response = await fetch(`${apiBaseUrl}/api/s3-upload`, {
        method: 'POST',
        headers: getAdminUploadHeaders(),
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUploadStatus('✓ S3 upload complete');
        
        // Check if CloudFront invalidation occurred
        if (data.data.cloudFrontInvalidationId) {
          setCdnEnabled(true);
          setInvalidationStatus('✓ CloudFront cache invalidated');
          setInvalidationId(data.data.cloudFrontInvalidationId);
        } else {
          setCdnEnabled(false);
          setInvalidationStatus('CloudFront not configured');
        }
        
        // Set both URLs separately
        setUploadedUrl(data.data.url);
        if (data.data.s3Url) {
          setS3Url(data.data.s3Url);
        }
        if (data.data.cdnUrl) {
          setCdnUrl(data.data.cdnUrl);
        }
        
        setSuccess(data.message);
        setSelectedFile(null);
        
        // Trigger image refresh
        setRefreshKey(Date.now());
        
        // Call the callback with the uploaded URL
        if (onUploadSuccess) {
          onUploadSuccess(data.data.url);
        }
      } else {
        setUploadStatus('✗ Upload failed');
        setError(data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadStatus('✗ Upload failed');
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCopyFilename = () => {
    navigator.clipboard.writeText(suggestedFilename);
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ color: '#FF6B35', fontWeight: 600 }}>
          {label}
        </Typography>
        <Tooltip title="Copy suggested filename">
          <IconButton
            size="small"
            onClick={handleCopyFilename}
            sx={{
              color: '#06AED5',
              p: 0.5,
              '&:hover': {
                background: 'rgba(6, 174, 213, 0.1)',
              },
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#06AED5', 
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            backgroundColor: 'rgba(6, 174, 213, 0.1)',
            px: 1,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          {suggestedFilename}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 1, py: 0 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={<CloudUploadIcon />}
          sx={{
            color: '#FF6B35',
            borderColor: '#FF6B35',
            '&:hover': {
              borderColor: '#e85f2f',
              background: 'rgba(255, 107, 53, 0.1)',
            },
          }}
        >
          Select Image
          <VisuallyHiddenInput
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
          />
        </Button>

        {selectedFile && (
          <>
            <Typography sx={{ color: '#2e7d32', fontWeight: 700, fontSize: '0.75rem' }}>
              {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              sx={{
                background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 70%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(45deg, #45a049 30%, #5cb85c 70%)',
                },
              }}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </>
        )}
      </Box>

      {/* Upload and Invalidation Status */}
      {(uploadStatus || invalidationStatus) && (
        <Box sx={{ mt: 1, p: 1.5, background: '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
          {uploadStatus && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: invalidationStatus ? 0.5 : 0 }}>
              <Typography 
                sx={{ 
                  color: uploadStatus.includes('✓') ? '#4CAF50' : uploadStatus.includes('✗') ? '#f44336' : '#FF6B35',
                  fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}
              >
                {uploadStatus}
              </Typography>
            </Box>
          )}
          {invalidationStatus && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography 
                sx={{ 
                  color: invalidationStatus.includes('✓') ? '#4CAF50' : '#999',
                  fontSize: '0.8rem',
                  fontWeight: cdnEnabled ? 'bold' : 'normal'
                }}
              >
                {invalidationStatus}
              </Typography>
              {invalidationId && (
                <Typography 
                  sx={{ 
                    color: '#666',
                    fontSize: '0.7rem',
                    fontStyle: 'italic'
                  }}
                >
                  (ID: {invalidationId})
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Display URLs with Copy Buttons */}
      {uploadedUrl && (
        <Box sx={{ mt: 1 }}>
          {/* S3 URL */}
          {s3Url && (
            <Box sx={{ mb: 1, p: 1, backgroundColor: '#e8f5e9', borderRadius: '6px', border: '1px solid #a5d6a7' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    S3 URL:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#388e3c', 
                      wordBreak: 'break-all',
                      fontSize: '0.7rem',
                      fontFamily: 'monospace'
                    }}
                  >
                    {s3Url}
                  </Typography>
                </Box>
                <Tooltip title="Copy S3 URL">
                  <IconButton 
                    onClick={() => handleCopyUrl(s3Url)} 
                    size="small" 
                    sx={{ 
                      color: '#2e7d32',
                      '&:hover': { background: 'rgba(46, 125, 50, 0.1)' }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* CloudFront URL */}
          {cdnUrl && (
            <Box sx={{ p: 1, backgroundColor: '#e3f2fd', borderRadius: '6px', border: '1px solid #90caf9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    CloudFront URL:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#1976d2', 
                      wordBreak: 'break-all',
                      fontSize: '0.7rem',
                      fontFamily: 'monospace'
                    }}
                  >
                    {cdnUrl}
                  </Typography>
                </Box>
                <Tooltip title="Copy CloudFront URL">
                  <IconButton 
                    onClick={() => handleCopyUrl(cdnUrl)} 
                    size="small" 
                    sx={{ 
                      color: '#1565c0',
                      '&:hover': { background: 'rgba(21, 101, 192, 0.1)' }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Current Image Preview */}
      <CurrentImageRender filename={suggestedFilename} refreshKey={refreshKey} />
    </Box>
  );
};

export default MagazineCoverUploader;
